<script lang="ts" setup>
import { v4 as uuid } from 'uuid'
import { useRouter } from 'vue-router/composables'
import type { RankingPageConfig } from '../configs/types'
import { useRanking } from '../composables/useRanking'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

interface Props {
  columnId: string
  config: RankingPageConfig
}

const props = defineProps<Props>()

const router = useRouter()

const {
  title,
  books,
  collections,
  fetching,
  ending,
  infiniteRef,
  homeBgUrl,
} = useRanking(props.columnId, props.config)

function gotoBook(id: string, firstChapterId?: string) {
  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: id, chapterId: firstChapterId || '' },
  })
}
</script>

<template>
  <div class="page h-100dvh overflow-scroll">
    <BasicNavbar class="top-2 color-n-text-2" />

    <div class="title color-n-text-1 h-60px">
      <span class="mx-2">{{ title || '' }}</span>
    </div>

    <div ref="infiniteRef" class="container bg-n-bg-n-primary rounded">
      <div v-for="(book, index) in collections" :key="index">
        <div
          class="flex items-center py-2"
          @click="gotoBook(book.id, book.firstChapter?.id)"
        >
          <div class="w-75px border-rd-8px overflow-hidden ml-1 shrink-0 relative">
            <BizBookCover
              :id="uuid()"
              :src="book.contentCoverUrl || ''"
              :extra="{
                module_name: books.showName,
                module_id: books.id,
                module_type: books.columnType,
                author: book.authorInfo.author,
                ...book,
              }"
              class="w-full h-full"
            />

            <div class="absolute top-0 left-2 bg-n-bg-n-primary text-10px text-n-text-inverse">
              <div class="relative w-18px h-18px">
                <img v-if="index === 0" :src="config.resources.goldMedal" class="medal">
                <img v-else-if="index === 1" :src="config.resources.silverMedal" class="medal">
                <img v-else-if="index === 2" :src="config.resources.bronze" class="medal">
                <img v-else :src="config.resources.medal" class="medal">

                <div class="w-18px h-18px absolute z-99 text-n-text-inverse text-12px left-0 top-0 flex items-center justify-center">
                  {{ index + 1 }}
                </div>
              </div>
            </div>
          </div>

          <div class="flex-1 overflow-hidden ml-3">
            <div class="font-500 truncate color-n-text-1">
              {{ book.contentName }}
            </div>
            <BizShowTags v-if="book.tagList && book.tagList.length" :tags="book.tagList" class="mt-2" />
            <div class="text-n-text-3 text-12px line-clamp-2 mt-2">
              {{ book.introduce }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="ending" class="py-15px text-13px text-n-text-3 text-center">
        {{ config.texts.noMore }}
      </div>
      <div v-else-if="fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3">
        <div class="animate-spin">
          <i-icon-park-outline-loading />
        </div>
        <p>{{ config.texts.loading }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page {
  background: v-bind(homeBgUrl) no-repeat top left;
  background-size: 100% auto;
  padding: 0 10px;
}

.title {
  font-family: HelveticaNeue, HelveticaNeue;
  font-weight: bold;
  font-size: 20px;
  text-align: center;
  font-style: normal;
  display: flex;
  align-items: center;
  justify-content: center;
}

.medal img{
  width: 100%;
  height: 100%;
}

.container {
  overflow-y: scroll;
  height: calc(100dvh - 70px);
  padding-top: 10px;
}
</style>
